import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft, Loader2, Wallet, Building2, FileText, AlertTriangle,
  Mail, Phone, MapPin, CalendarDays, BadgeCheck, User, ChevronRight,
  Pencil, Power, Save, X, Plus, PieChart, DollarSign, Truck, Send, Clock,
  Users, StickyNote, Target, ListChecks, Pill, Paperclip, Trash2,
} from 'lucide-react';
import { INV_STATUS, statusLabel } from './pmStatus';
import ParticipantEmail from '../Mail/ParticipantEmail';

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return '—'; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Premium KPI card — icon tile + label + value.
const KPI_TONE = {
  sky: 'bg-sky-50 text-sky-600', blue: 'bg-blue-50 text-blue-600', cyan: 'bg-cyan-50 text-cyan-600',
  indigo: 'bg-indigo-50 text-indigo-600', violet: 'bg-violet-50 text-violet-600', fuchsia: 'bg-fuchsia-50 text-fuchsia-600',
};
const KPI = ({ label, value, icon: Icon, tone = 'sky', alert }) => (
  <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2">
      <span className={clsx('w-8 h-8 rounded-xl flex items-center justify-center', KPI_TONE[tone] || KPI_TONE.sky)}>{Icon && <Icon size={15} />}</span>
      <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wide leading-tight">{label}</span>
    </div>
    <div className={clsx('text-xl font-extrabold tabular-nums mt-2.5 leading-none', alert ? 'text-rose-600' : 'text-slate-800')}>{value}</div>
  </div>
);

// One panel for the six inventory sections. Each is fetched only when its tab is first opened —
// the profile already makes a dozen queries on load, and nobody opens all ten sections.
//
// Read-only on purpose. This is the finance side of the record: a plan manager needs to KNOW a
// participant has four medications and who their nominee is; editing the care record belongs on
// the client's own screens, where the people responsible for it work.
const SECTION_COLUMNS = {
  relationships: [
    ['Name', (r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—'],
    ['Relationship', (r) => cap(r.relation_category) || '—'],
    ['Email', (r) => r.email || '—'],
    ['Phone', (r) => r.phone || '—'],
    ['Role', (r) => [r.is_primary && 'Primary', r.is_billing_contact && 'Billing'].filter(Boolean).join(' · ') || '—'],
  ],
  notes: [
    ['Date', (r) => fmtD(r.created_at)],
    ['Type', (r) => cap(r.note_type) || '—'],
    ['Author', (r) => r.author || '—'],
    ['Length', (r) => `${Number(r.length || 0).toLocaleString()} chars`],
  ],
  goals: [
    ['Goal', (r) => r.name || '—'],
    ['Commenced', (r) => fmtD(r.commenced_at)],
    ['Achieved', (r) => (r.achieved_at ? fmtD(r.achieved_at) : r.closed_at ? `Closed ${fmtD(r.closed_at)}` : 'In progress')],
    ['Rating', (r) => (r.rating != null ? String(r.rating) : '—')],
  ],
  tasks: [
    ['Task', (r) => r.title || '—'],
    ['Type', (r) => cap(r.task_type) || '—'],
    ['Assignee', (r) => r.assignee || 'Unassigned'],
    ['Due', (r) => fmtD(r.due_date)],
    ['Status', (r) => cap(r.status) || '—'],
  ],
  medications: [
    ['Medication', (r) => r.medication_name || '—'],
    ['Dosage', (r) => r.dosage || '—'],
    ['Frequency', (r) => (r.is_prn ? 'PRN (as needed)' : r.frequency || '—')],
    ['Route', (r) => cap(r.route) || '—'],
    ['Status', (r) => (r.is_active === false ? 'Ceased' : 'Active')],
  ],
  documents: [
    ['Document', (r) => r.name || '—'],
    ['Type', (r) => cap(r.document_type) || '—'],
    ['Expires', (r) => fmtD(r.expiry_date)],
    ['Added', (r) => fmtD(r.created_at)],
  ],
};
const EMPTY_HINT = {
  relationships: 'No contacts recorded — a nominee or billing contact is added on the client record.',
  notes: 'No progress notes have been written for this participant.',
  goals: 'No goals recorded.',
  tasks: 'No tasks for this participant.',
  medications: 'No medications recorded.',
  documents: 'No documents on file.',
};
function SectionPanel({ clientId, name, icon: Icon, title }) {
  const [rows, setRows] = useState(undefined);
  useEffect(() => {
    let alive = true;
    setRows(undefined);
    api.get(`/plan-management/participants/${clientId}/section/${name}`)
      .then((r) => { if (alive) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [clientId, name]);
  const cols = SECTION_COLUMNS[name] || [];
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Icon size={15} className="text-primary-600" /> {title}</h3>
        {rows !== undefined && <span className="text-[11px] text-slate-400 tabular-nums">{rows.length} record{rows.length === 1 ? '' : 's'}</span>}
      </div>
      {rows === undefined ? <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-300" /></div>
        : rows.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">{EMPTY_HINT[name] || 'Nothing recorded.'}</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
                  <tr>{cols.map(([h]) => <th key={h} className="px-2.5 py-2 font-semibold text-left whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      {cols.map(([h, get]) => <td key={h} className="px-2.5 py-2 text-slate-600 align-top"><span className="block max-w-[280px] truncate">{get(r)}</span></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </>
  );
}

// The invoices behind the Client Alerts count — on hold, claim error, or declined by the
// participant. Each row says WHY, because "on hold" without the reason is not an alert, it is a
// second thing to go and look up.
function AlertsList({ clientId, onOpen }) {
  const [rows, setRows] = useState(undefined);
  useEffect(() => {
    let alive = true;
    api.get(`/plan-management/participants/${clientId}/section/alerts`)
      .then((r) => { if (alive) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [clientId]);
  if (rows === undefined) return <div className="py-8 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-300" /></div>;
  if (rows.length === 0) return <p className="text-sm text-slate-400 py-6 text-center">No invoices on hold, in claim error, or declined.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-700 text-[10px] uppercase tracking-wide text-slate-200">
          <tr>
            <th className="px-2.5 py-2 font-semibold text-left">Reference</th>
            <th className="px-2.5 py-2 font-semibold text-left">Provider</th>
            <th className="px-2.5 py-2 font-semibold text-right">Amount</th>
            <th className="px-2.5 py-2 font-semibold text-left">State</th>
            <th className="px-2.5 py-2 font-semibold text-left">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-primary-50/40 cursor-pointer" onClick={() => onOpen(r.id)}>
              <td className="px-2.5 py-2 font-mono text-primary-700 font-semibold whitespace-nowrap">{r.reference || '—'}</td>
              <td className="px-2.5 py-2 text-slate-700"><span className="block max-w-[180px] truncate">{r.provider_name || '—'}</span></td>
              <td className="px-2.5 py-2 text-right tabular-nums font-semibold">{money(r.total_amount)}</td>
              <td className="px-2.5 py-2"><span className={clsx('badge !text-[10px]', INV_STATUS[r.status] || 'badge-gray')}>{statusLabel(r.status)}</span></td>
              <td className="px-2.5 py-2 text-slate-500"><span className="block max-w-[320px] truncate" title={r.claim_error_reason || r.block_reason || ''}>{r.claim_error_reason || r.block_reason || '—'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Detail = ({ label, value, icon: Icon }) => (
  <div className="min-w-0 rounded-xl bg-slate-50/70 border border-slate-100 px-3.5 py-2.5">
    <span className="text-[10.5px] uppercase tracking-wide text-slate-400 font-bold flex items-center gap-1">{Icon && <Icon size={11} />}{label}</span>
    <span className="text-sm font-medium text-slate-800 break-words block mt-1">{value || '—'}</span>
  </div>
);

export default function ParticipantProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prof, setProf] = useState(undefined); // undefined=loading, null=error
  // ?tab= makes every section on this profile addressable, so another screen can link straight to
  // the one it means instead of dropping the operator on the default and hoping they find it.
  // The section rail drives LOCAL state, and the URL follows it. Deriving the active section from
  // the query string alone made every click depend on the router round-tripping a replace() before
  // anything re-rendered — one dependency too many for a rail whose whole job is to switch panes
  // instantly. ?tab= still deep-links in (read once, below) so links from other screens keep working.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(() => searchParams.get('tab') || 'funding');
  const setTab = (t) => {
    setTabState(t);
    const n = new URLSearchParams(searchParams);
    if (t === 'funding') n.delete('tab'); else n.set('tab', t);
    setSearchParams(n, { replace: true });
  };
  // A ?tab= arriving after mount (someone follows a link while already on this profile) still wins.
  const urlTab = searchParams.get('tab');
  useEffect(() => { if (urlTab) setTabState(urlTab); }, [urlTab]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [allProviders, setAllProviders] = useState([]);
  const [assignPick, setAssignPick] = useState('');

  const load = useCallback(() => {
    setProf(undefined);
    api.get(`/plan-management/participants/${id}/profile`)
      .then((r) => { setProf(r.data); setForm(r.data?.client || {}); })
      .catch(() => setProf(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/plan-management/providers').then((r) => setAllProviders(r.data || [])).catch(() => {}); }, []);

  // Participant portal access (standalone /participant login — separate from the family portal).
  const [portalAccounts, setPortalAccounts] = useState([]);
  const [invite, setInvite] = useState({ name: '', email: '' });
  const [portalBusy, setPortalBusy] = useState('');
  const loadPortal = useCallback(() => {
    api.get('/pm-portal/accounts', { params: { client_id: id } }).then((r) => setPortalAccounts(r.data || [])).catch(() => {});
  }, [id]);
  useEffect(() => { loadPortal(); }, [loadPortal]);
  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error('Enter an email address'); return; }
    setPortalBusy('invite');
    try {
      const r = await api.post('/pm-portal/accounts', { client_id: id, name: invite.name.trim(), email: invite.email.trim() });
      toast.success(r.data.emailed ? 'Invite emailed' : 'Account created — email failed, copy the link from the browser console');
      if (!r.data.emailed && r.data.invite_url) console.info('[participant portal invite]', r.data.invite_url);
      setInvite({ name: '', email: '' }); loadPortal();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };
  const resendInvite = async (aid) => {
    setPortalBusy('r' + aid);
    try { const r = await api.post(`/pm-portal/accounts/${aid}/resend`, {}); toast.success(r.data.emailed ? 'Invite re-sent' : 'New link generated — email failed'); loadPortal(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };
  const revokeAccess = async (aid) => {
    setPortalBusy('x' + aid);
    try { await api.delete(`/pm-portal/accounts/${aid}`); toast.success('Access revoked'); loadPortal(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setPortalBusy(''); }
  };

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/plan-management/participants/${id}`, {
        title: form.title, first_name: form.first_name, last_name: form.last_name, date_of_birth: form.date_of_birth, gender: form.gender, ndis_number: form.ndis_number,
        service_type: form.service_type, service_region: form.service_region, pace_participant: !!form.pace_participant, pm_approval_required: !!form.pm_approval_required, pm_fee_rate: form.pm_fee_rate,
        email: form.email, phone: form.phone, mobile: form.mobile, additional_emails: form.additional_emails, address: form.address, suburb: form.suburb, state: form.state, postcode: form.postcode,
        primary_disability: form.primary_disability, other_disability: form.other_disability, cultural_requirements: form.cultural_requirements, atsi_status: form.atsi_status, consent_to_audit: !!form.consent_to_audit, referred_by: form.referred_by, interests: form.interests,
        pm_agreement_signed: form.pm_agreement_signed || null, pm_agreement_ref: form.pm_agreement_ref || null,
        auto_approve_invoices: !!form.auto_approve_invoices, statement_opt_out: !!form.statement_opt_out, postal_statement: !!form.postal_statement, dcp_client: !!form.dcp_client,
        plan_period_start: form.plan_period_start, plan_period_end: form.plan_period_end,
      });
      toast.success('Participant updated'); setEditing(false); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); } finally { setSavingEdit(false); }
  };
  const toggleActive = async () => {
    const c = prof?.client || {};
    try { await api.put(`/plan-management/participants/${id}`, { is_active: !c.is_active }); toast.success(c.is_active ? 'Participant deactivated' : 'Participant reactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  // Hard delete. The API refuses the moment there is any invoice and says so, so this button is
  // only ever an unwind of a data-entry mistake — the 409 message is surfaced verbatim rather than
  // replaced with a generic failure, because it names the alternative (deactivate).
  const deleteParticipant = async () => {
    const c = prof?.client || {};
    if (!window.confirm(`Permanently delete ${c.name || 'this participant'}? This is only possible while they have no invoices — deactivate them instead if you want to keep their history.`)) return;
    try {
      await api.delete(`/plan-management/participants/${id}`);
      toast.success('Participant deleted');
      navigate('/plan-management?view=participants');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete'); }
  };
  const toggleAutoApprove = async () => {
    const c = prof?.client || {};
    try { await api.put(`/plan-management/participants/${id}`, { auto_approve_invoices: !c.auto_approve_invoices }); toast.success(c.auto_approve_invoices ? 'Auto-approval turned OFF — new invoices need manual approval' : 'Auto-approval turned ON — new invoices pre-approved'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const openInvoice = (invId) => navigate(`/plan-management?openInvoice=${invId}`);
  const newContract = async () => {
    try { const r = await api.post(`/plan-management/participants/${id}/contracts`, { contract_status: 'new', funding_source: 'NDIS' }); toast.success('Contract created — add the details'); navigate(`/plan-management/contracts/${r.data.id}`); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const assignProvider = async (providerId) => {
    if (!providerId) return;
    try { await api.post(`/plan-management/participants/${id}/providers`, { provider_id: providerId }); toast.success('Provider assigned'); setAssignPick(''); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const unassignProvider = async (providerId) => {
    if (!window.confirm('Remove this provider from the participant?')) return;
    try { await api.delete(`/plan-management/participants/${id}/providers/${providerId}`); toast.success('Provider removed'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const deactivateContract = async (planId, e) => {
    e?.stopPropagation();
    try { await api.put(`/plan-management/contracts/${planId}`, { contract_status: 'inactive' }); toast.success('Contract deactivated'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  const deleteContract = async (planId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this contract? Only works if it has no invoices.')) return;
    try { await api.delete(`/plan-management/contracts/${planId}`); toast.success('Contract deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (prof === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (!prof) return (
    <div className="p-6 max-w-3xl mx-auto">
      <button onClick={() => navigate('/plan-management')} className="text-sm text-primary-600 flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Back to Plan Management</button>
      <div className="rounded-2xl border border-rose-100 bg-rose-50 p-8 text-center text-rose-700">Could not load this participant profile.</div>
    </div>
  );

  const c = prof.client || {};
  const k = prof.kpis || {};
  const serviceType = c.client_type === 'plan_managed' ? 'Plan Management Client'
    : c.client_type === 'support_coordination' ? 'Support Coordination Client'
    : cap(c.client_type) || '—';
  // The section inventory, every entry carrying its count INCLUDING zero — the point of the rail is
  // that you can see what a participant does and does not have without opening anything.
  // `Medical History` from the reference has no store of its own here (disability and cultural
  // fields live in Participant details above), so it is absent rather than a link to nothing.
  const n = prof.counts || {};
  const tabs = [
    { key: 'funding', label: 'Client Funding', n: n.funding ?? (prof.contracts?.length || 0), icon: Wallet },
    { key: 'providers', label: 'Individual Providers', n: n.providers ?? (prof.assigned_providers?.length || 0), icon: Building2 },
    { key: 'invoices', label: 'Invoices', n: n.invoices ?? (prof.invoices?.length || 0), icon: FileText },
    { key: 'alerts', label: 'Client Alerts', n: n.alerts || 0, icon: AlertTriangle },
    { key: 'email', label: 'Email', n: '', icon: Mail },
    { key: 'relationships', label: 'Relationships', n: n.relationships || 0, icon: Users },
    { key: 'notes', label: 'Notes', n: n.notes || 0, icon: StickyNote },
    { key: 'goals', label: 'Client Goals', n: n.goals || 0, icon: Target },
    { key: 'tasks', label: 'Client Tasks', n: n.tasks || 0, icon: ListChecks },
    { key: 'medications', label: 'Medications', n: n.medications || 0, icon: Pill },
    { key: 'documents', label: 'Documents', n: n.documents || 0, icon: Paperclip },
  ];
  const EI = 'input-field w-full text-sm';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/plan-management')} className="text-slate-500 hover:text-slate-800 flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> Plan Management</button>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-sm font-semibold text-slate-800 truncate">{c.name}</span>
          <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{serviceType}</span>
          {c.pace_participant && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">PACE</span>}
          {c.is_active === false && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Inactive</span>}
          <span className="flex-1" />
          <span className={clsx('hidden md:inline text-[11px] font-semibold px-2.5 py-1 rounded-full mr-1', k.remaining < 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-50 text-emerald-700')}>
            {k.remaining < 0 ? 'Over-allocated' : 'Available'}: {money(k.remaining)}
          </span>
          {editing ? (
            <>
              <button onClick={() => { setEditing(false); setForm(prof.client || {}); }} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><X size={14} /> Cancel</button>
              <button onClick={saveEdit} disabled={savingEdit} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5 disabled:opacity-50">{savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>
            </>
          ) : (
            <>
              <button onClick={toggleAutoApprove} title="Pre-approve all new invoices for this participant" className={clsx('!text-xs !px-3 !py-1.5 rounded-lg font-medium border flex items-center gap-1.5', c.auto_approve_invoices ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700')}><BadgeCheck size={13} /> Auto-approve: {c.auto_approve_invoices ? 'On' : 'Off'}</button>
              <button onClick={toggleActive} className={clsx('!text-xs !px-3 !py-1.5 rounded-lg font-medium border flex items-center gap-1.5', c.is_active === false ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600')}><Power size={13} /> {c.is_active === false ? 'Reactivate' : 'Deactivate'}</button>
              <button onClick={deleteParticipant} title="Only possible while this participant has no invoices" className="!text-xs !px-3 !py-1.5 rounded-lg font-medium border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 flex items-center gap-1.5"><Trash2 size={13} /> Delete</button>
              <button onClick={() => { setForm(prof.client || {}); setEditing(true); }} className="btn-primary !text-xs !px-3 !py-1.5 flex items-center gap-1.5"><Pencil size={13} /> Edit</button>
            </>
          )}
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-6">
        {/* Premium hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-600 p-6 sm:p-7 shadow-lg shadow-indigo-500/20">
          <div className="absolute -right-16 -top-20 w-64 h-64 rounded-full bg-white/10" />
          <div className="absolute right-10 -bottom-24 w-52 h-52 rounded-full bg-violet-400/25" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
            {c.profile_photo
              ? <img src={c.profile_photo} alt="" className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/20 shrink-0" />
              : <div className="w-20 h-20 rounded-2xl bg-white/15 ring-4 ring-white/10 flex items-center justify-center text-white shrink-0 text-2xl font-extrabold">{(c.name || '?').trim().charAt(0).toUpperCase()}</div>}
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-extrabold text-white leading-tight truncate">{c.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white">{serviceType}</span>
                {c.pace_participant && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/15 text-white">PACE</span>}
                {c.ndis_number && <span className="text-[11px] font-medium text-indigo-100">NDIS {c.ndis_number}</span>}
                {c.is_active === false && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white">Inactive</span>}
              </div>
            </div>
            <div className="flex gap-6 sm:gap-8 shrink-0">
              <div>
                <p className="text-[10.5px] font-bold uppercase tracking-wide text-indigo-100">Available</p>
                <p className={clsx('text-2xl font-extrabold tabular-nums leading-tight mt-0.5', k.remaining < 0 ? 'text-rose-200' : 'text-white')}>{money(k.remaining)}</p>
              </div>
              <div className="hidden sm:block">
                <p className="text-[10.5px] font-bold uppercase tracking-wide text-indigo-100">Total funding</p>
                <p className="text-2xl font-extrabold tabular-nums leading-tight mt-0.5 text-white/90">{money(k.funding)}</p>
              </div>
            </div>
          </div>
          {k.funding > 0 && (
            <div className="relative mt-5">
              <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full bg-amber-300" style={{ width: `${Math.max(2, Math.min(100, (k.allocation / k.funding) * 100))}%` }} />
              </div>
              <p className="text-[11px] text-indigo-100 mt-1.5">{money(k.allocation)} allocated of {money(k.funding)} · {money(k.delivered)} delivered</p>
            </div>
          )}
        </div>

        {/* Premium KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KPI label="Total Funding"    value={money(k.funding)}    icon={Wallet}     tone="sky" />
          <KPI label="Total Allocation" value={money(k.allocation)} icon={PieChart}   tone="blue" />
          <KPI label="Total Remaining"  value={money(k.remaining)}  icon={DollarSign} tone="cyan" alert={k.remaining < 0} />
          <KPI label="Total Delivered"  value={money(k.delivered)}  icon={Truck}      tone="indigo" />
          <KPI label="Total Claimed"    value={money(k.claimed)}    icon={Send}       tone="violet" />
          <KPI label="Total Unclaimed"  value={money(k.unclaimed)}  icon={Clock}      tone="fuchsia" />
        </div>

        {/* Identity + details */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr]">
            {/* left: section nav rail */}
            <div className="p-4 border-b lg:border-b-0 lg:border-r border-slate-100 bg-slate-50/60">
              <p className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold px-2 mb-2">Sections</p>
              <div className="space-y-0.5">
                {tabs.map((t) => {
                  const Icon = t.icon; const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)}
                      className={clsx('w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition', active ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white')}>
                      <span className="flex items-center gap-2"><Icon size={15} /> {t.label}</span>
                      <span className={clsx('text-xs', active ? 'text-white/80' : 'text-slate-400')}>{t.n}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* right: details grid (read-only) OR edit form */}
            <div className="p-6">
              <h2 className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-4">Participant details</h2>
              {!editing ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  <Detail label="Name" value={[c.title, c.name].filter(Boolean).join(' ')} icon={User} />
                  <Detail label="Date of Birth" value={fmtD(c.date_of_birth)} icon={CalendarDays} />
                  <Detail label="Gender" value={c.gender} />
                  <Detail label="NDIS Number" value={c.ndis_number} icon={BadgeCheck} />
                  <Detail label="Plan Start" value={fmtD(c.plan_period_start)} />
                  <Detail label="Plan End" value={fmtD(c.plan_period_end)} />
                  <Detail label="Address" value={c.address_full} icon={MapPin} />
                  <Detail label="Email" value={c.email} icon={Mail} />
                  <Detail label="Phone" value={c.phone} icon={Phone} />
                  <Detail label="Mobile" value={c.mobile} icon={Phone} />
                  <Detail label="Service Type" value={serviceType} />
                  <Detail label="Service Region" value={c.service_region} />
                  <Detail label="PACE Participant" value={c.pace_participant ? 'Yes' : 'No'} />
                  <Detail label="Approval Policy" value={c.pm_approval_required ? 'Required' : 'Optional'} />
                  <Detail label="Plan Management Rate" value={c.pm_fee_rate != null ? money(c.pm_fee_rate) : '—'} />
                  <Detail label="Additional Emails" value={c.additional_emails} />
                  <Detail label="Primary Disability" value={c.primary_disability} />
                  <Detail label="Other Disability" value={c.other_disability} />
                  <Detail label="Cultural Requirements" value={c.cultural_requirements} />
                  <Detail label="Interests" value={c.interests} />
                  <Detail label="ATSI Status" value={c.atsi_status} />
                  <Detail label="Referred By" value={c.referred_by} />
                  <Detail label="Consents to Auditing" value={c.consent_to_audit ? 'Yes' : 'No'} />
                  <Detail label="Service Agreement Signed" value={c.pm_agreement_signed ? fmtD(c.pm_agreement_signed) : 'Not recorded'} />
                  {c.pm_agreement_ref ? <Detail label="Agreement Reference" value={c.pm_agreement_ref} /> : null}
                  <Detail label="Auto-Approve Invoices" value={c.auto_approve_invoices ? 'Yes' : 'No'} />
                  <Detail label="Monthly Statement Opt-Out" value={c.statement_opt_out ? 'Yes' : 'No'} />
                  <Detail label="Postal Statement" value={c.postal_statement ? 'Yes' : 'No'} />
                  <Detail label="DCP Client" value={c.dcp_client ? 'Yes' : 'No'} />
                  <Detail label="Status" value={c.is_active === false ? 'Inactive' : 'Active'} />
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div><label className="text-[11px] text-slate-500">Title</label><input className={EI} value={form.title || ''} onChange={setF('title')} /></div>
                  <div><label className="text-[11px] text-slate-500">First name</label><input className={EI} value={form.first_name || ''} onChange={setF('first_name')} /></div>
                  <div><label className="text-[11px] text-slate-500">Last name</label><input className={EI} value={form.last_name || ''} onChange={setF('last_name')} /></div>
                  <div><label className="text-[11px] text-slate-500">Date of birth</label><input type="date" className={EI} value={(form.date_of_birth || '').slice(0, 10)} onChange={setF('date_of_birth')} /></div>
                  <div><label className="text-[11px] text-slate-500">Gender</label><select className={EI} value={form.gender || ''} onChange={setF('gender')}><option value="">—</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Other</option></select></div>
                  <div><label className="text-[11px] text-slate-500">NDIS number</label><input className={EI} value={form.ndis_number || ''} onChange={setF('ndis_number')} /></div>
                  <div><label className="text-[11px] text-slate-500">Service type</label><select className={EI} value={form.service_type || 'plan_managed'} onChange={setF('service_type')}><option value="plan_managed">Plan Management Client</option><option value="support_coordination">Support Coordination Client</option></select></div>
                  <div><label className="text-[11px] text-slate-500">Service region</label><select className={EI} value={form.service_region || ''} onChange={setF('service_region')}><option value="">—</option><option>NSW</option><option>VIC</option><option>QLD</option><option>SA</option><option>WA</option><option>TAS</option><option>ACT</option><option>NT</option><option>Remote</option><option>Very remote</option></select></div>
                  <div><label className="text-[11px] text-slate-500">Plan mgmt rate</label><input type="number" step="0.01" className={EI} value={form.pm_fee_rate ?? ''} onChange={setF('pm_fee_rate')} /></div>
                  <div><label className="text-[11px] text-slate-500">Plan start</label><input type="date" className={EI} value={(form.plan_period_start || '').slice(0, 10)} onChange={setF('plan_period_start')} /></div>
                  <div><label className="text-[11px] text-slate-500">Plan end</label><input type="date" className={EI} value={(form.plan_period_end || '').slice(0, 10)} onChange={setF('plan_period_end')} /></div>
                  <div><label className="text-[11px] text-slate-500">Email</label><input type="email" className={EI} value={form.email || ''} onChange={setF('email')} /></div>
                  <div><label className="text-[11px] text-slate-500">Phone</label><input className={EI} value={form.phone || ''} onChange={setF('phone')} /></div>
                  <div><label className="text-[11px] text-slate-500">Mobile</label><input className={EI} value={form.mobile || ''} onChange={setF('mobile')} /></div>
                  <div><label className="text-[11px] text-slate-500">Additional emails</label><input className={EI} value={form.additional_emails || ''} onChange={setF('additional_emails')} /></div>
                  <div className="col-span-2"><label className="text-[11px] text-slate-500">Address</label><input className={EI} value={form.address || ''} onChange={setF('address')} /></div>
                  <div><label className="text-[11px] text-slate-500">Suburb</label><input className={EI} value={form.suburb || ''} onChange={setF('suburb')} /></div>
                  <div><label className="text-[11px] text-slate-500">State</label><input className={EI} value={form.state || ''} onChange={setF('state')} /></div>
                  <div><label className="text-[11px] text-slate-500">Postcode</label><input className={EI} value={form.postcode || ''} onChange={setF('postcode')} /></div>
                  <div><label className="text-[11px] text-slate-500">Primary disability</label><input className={EI} value={form.primary_disability || ''} onChange={setF('primary_disability')} /></div>
                  <div><label className="text-[11px] text-slate-500">Other disability</label><input className={EI} value={form.other_disability || ''} onChange={setF('other_disability')} /></div>
                  <div><label className="text-[11px] text-slate-500">Cultural requirements</label><input className={EI} value={form.cultural_requirements || ''} onChange={setF('cultural_requirements')} /></div>
                  <div><label className="text-[11px] text-slate-500">Interests</label><input className={EI} value={form.interests || ''} onChange={setF('interests')} /></div>
                  <div><label className="text-[11px] text-slate-500">ATSI status</label><select className={EI} value={form.atsi_status || ''} onChange={setF('atsi_status')}><option value="">—</option><option>Aboriginal</option><option>Torres Strait Islander</option><option>Both</option><option>Neither</option></select></div>
                  <div><label className="text-[11px] text-slate-500">Referred by</label><input className={EI} value={form.referred_by || ''} onChange={setF('referred_by')} /></div>
                  <div><label className="text-[11px] text-slate-500">Service agreement signed</label><input type="date" className={EI} value={String(form.pm_agreement_signed || '').slice(0, 10)} onChange={setF('pm_agreement_signed')} /></div>
                  <div><label className="text-[11px] text-slate-500">Agreement reference / link</label><input className={EI} placeholder="Doc name, link or note" value={form.pm_agreement_ref || ''} onChange={setF('pm_agreement_ref')} /></div>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.pace_participant} onChange={setF('pace_participant')} /> PACE participant</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.pm_approval_required} onChange={setF('pm_approval_required')} /> Requires approval</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.consent_to_audit} onChange={setF('consent_to_audit')} /> Consents to auditing</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.auto_approve_invoices} onChange={setF('auto_approve_invoices')} /> Auto-approve invoices</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.statement_opt_out} onChange={setF('statement_opt_out')} /> Statement opt-out</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.postal_statement} onChange={setF('postal_statement')} /> Postal statement</label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2"><input type="checkbox" checked={!!form.dcp_client} onChange={setF('dcp_client')} /> DCP client</label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Forecast banner */}
        {prof.forecast && (
          <div className={clsx('rounded-2xl border p-4 shadow-sm', prof.forecast.variance < 0 ? 'bg-rose-50 border-rose-100' : 'bg-white border-slate-200')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><AlertTriangle size={15} className={prof.forecast.variance < 0 ? 'text-rose-500' : 'text-slate-400'} /> Budget forecast</span>
              {prof.forecast.pace != null && <span className={clsx('text-[11px] font-semibold px-2.5 py-1 rounded-full', prof.forecast.pace > 105 ? 'bg-rose-100 text-rose-700' : prof.forecast.pace < 95 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')}>{prof.forecast.pace > 105 ? 'Ahead of pace' : prof.forecast.pace < 95 ? 'Underspending' : 'On track'} ({prof.forecast.pace}%)</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-slate-400 block text-xs">Burn / day</span><span className="font-semibold tabular-nums">{money(prof.forecast.perDay)}</span></div>
              <div><span className="text-slate-400 block text-xs">Projected at end</span><span className="font-semibold tabular-nums">{money(prof.forecast.projected)}</span></div>
              <div><span className="text-slate-400 block text-xs">{prof.forecast.variance < 0 ? 'Projected overspend' : 'Projected underspend'}</span><span className={clsx('font-semibold tabular-nums', prof.forecast.variance < 0 ? 'text-rose-600' : 'text-emerald-600')}>{money(Math.abs(prof.forecast.variance))}</span></div>
              <div><span className="text-slate-400 block text-xs">Funds run out</span><span className="font-semibold">{prof.forecast.runout_date ? fmtD(prof.forecast.runout_date) : '—'}</span></div>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">{prof.forecast.elapsed} of {prof.forecast.totalDays} days elapsed · {prof.forecast.remainingDays} remaining</div>
          </div>
        )}

        {/* Tabbed section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          {tab === 'funding' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Wallet size={15} className="text-primary-600" /> Client Funding — Service Contracts</h3>
                <button onClick={newContract} className="btn-secondary !text-xs !px-3 !py-1.5">+ New contract</button>
              </div>
              {(prof.contracts?.length || 0) === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No funding contracts.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                      <tr>{['Contract', 'Status', 'Start', 'End', 'Funding', 'Allocated', 'Remaining', 'Delivered', 'Claimed', 'Unclaimed'].map((h, i) => <th key={h} className={clsx('px-2.5 py-2 font-semibold', i < 4 ? 'text-left' : 'text-right')}>{h}</th>)}<th className="px-2.5 py-2 text-right font-semibold">Actions</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {prof.contracts.map((c2) => (
                        <tr key={c2.id} className="hover:bg-primary-50/40 cursor-pointer" onClick={() => navigate(`/plan-management/contracts/${c2.id}`)}>
                          <td className="px-2.5 py-2 font-mono text-primary-700 font-semibold">{c2.ref}</td>
                          <td className="px-2.5 py-2"><span className={clsx('badge !text-[10px]', c2.status === 'Inactive' ? 'badge-gray' : 'badge-green')}>{c2.status || 'Active'}</span></td>
                          <td className="px-2.5 py-2">{fmtD(c2.start_date)}</td>
                          <td className="px-2.5 py-2">{fmtD(c2.end_date)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums">{money(c2.funding)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums">{money(c2.allocated)}</td>
                          <td className={clsx('px-2.5 py-2 text-right tabular-nums font-semibold', c2.remaining < 0 ? 'text-rose-600' : 'text-slate-700')}>{money(c2.remaining)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums">{money(c2.delivered)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums">{money(c2.claimed)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums">{money(c2.unclaimed)}</td>
                          <td className="px-2.5 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            {c2.status !== 'Inactive' && <button onClick={(e) => deactivateContract(c2.id, e)} className="text-[11px] text-slate-500 hover:text-amber-600 mr-2">Deactivate</button>}
                            <button onClick={(e) => deleteContract(c2.id, e)} title="Delete (only if no invoices)" className="text-slate-300 hover:text-rose-600 align-middle"><X size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {tab === 'providers' && (
            <>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Building2 size={15} className="text-primary-600" /> Individual Providers</h3>
                <div className="flex items-center gap-2">
                  <select value={assignPick} onChange={(e) => setAssignPick(e.target.value)} className="input-field !text-xs !py-1.5 !w-56">
                    <option value="">Assign a provider…</option>
                    {allProviders.filter((p) => !(prof.assigned_providers || []).some((a) => a.id === p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button onClick={() => assignProvider(assignPick)} disabled={!assignPick} className="btn-secondary !text-xs !px-3 !py-1.5 flex items-center gap-1.5 disabled:opacity-40"><Plus size={13} /> Assign</button>
                </div>
              </div>
              {(prof.assigned_providers?.length || 0) === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No providers assigned yet — pick one above to assign it to this participant.</p>
              ) : (
                <div className="divide-y divide-slate-50">
                  {prof.assigned_providers.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2.5 gap-3">
                      <button onClick={() => navigate(`/plan-management/providers/${p.id}`)} className="text-sm text-slate-700 font-medium flex items-center gap-2 hover:text-primary-700 min-w-0 text-left">
                        <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Building2 size={14} /></span>
                        <span className="truncate">{p.name}{p.abn ? <span className="text-slate-400 font-normal"> · ABN {p.abn}</span> : ''}</span>
                      </button>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm text-slate-500 tabular-nums">{p.invoices} inv · <span className="font-semibold text-slate-700">{money(p.total)}</span></span>
                        <button onClick={() => unassignProvider(p.id)} title="Remove provider" className="text-slate-300 hover:text-rose-600"><X size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {tab === 'invoices' && (
            <>
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><FileText size={15} className="text-primary-600" /> Invoices</h3>
              {(prof.invoices?.length || 0) === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No invoices.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400"><tr><th className="px-2.5 py-2 text-left font-semibold">Date</th><th className="px-2.5 py-2 text-left font-semibold">Provider</th><th className="px-2.5 py-2 text-right font-semibold">Amount</th><th className="px-2.5 py-2 text-left font-semibold">Status</th></tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {prof.invoices.map((v) => (
                        <tr key={v.id} className="hover:bg-primary-50/40 cursor-pointer" onClick={() => openInvoice(v.id)}>
                          <td className="px-2.5 py-2 text-slate-600">{fmtD(v.invoice_date)}</td>
                          <td className="px-2.5 py-2 text-slate-700">{v.provider}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums font-semibold">{money(v.total_amount)}</td>
                          <td className="px-2.5 py-2"><span className={clsx('badge !text-[10px]', INV_STATUS[v.status] || 'badge-gray')}>{statusLabel(v.status)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {/* Client Alerts — computed from the ledger, not a stored list. These are the money
              conditions the participant is in RIGHT NOW; a stored table would go stale the moment
              an invoice moved. Clicking one opens the invoice that caused it. */}
          {tab === 'email' && <ParticipantEmail bare clientId={id} name={c.name} to={c.email ? [{ name: c.name, address: c.email }] : []} />}
          {tab === 'alerts' && (
            <>
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><AlertTriangle size={15} className="text-amber-500" /> Client Alerts</h3>
              {k.funding > 0 && k.remaining < 0 && (
                <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[12.5px] text-rose-700 flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Committed spend exceeds funding by <strong className="tabular-nums">{money(Math.abs(k.remaining))}</strong> across this participant&apos;s live contracts.</span>
                </div>
              )}
              <AlertsList clientId={id} onOpen={openInvoice} />
            </>
          )}
          {['relationships', 'notes', 'goals', 'tasks', 'medications', 'documents'].includes(tab) && (
            <SectionPanel
              clientId={id} name={tab}
              icon={(tabs.find((t) => t.key === tab) || {}).icon || FileText}
              title={(tabs.find((t) => t.key === tab) || {}).label || ''}
            />
          )}
        </div>

        {/* Participant portal access — the standalone /participant login (budgets + approvals) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><User size={15} className="text-primary-600" /> Participant portal access</h3>
            <span className="text-[11px] text-slate-400">Participant signs in at <span className="font-mono text-slate-500">/participant</span> — live budget, invoices, one-tap approvals</span>
          </div>
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div><label className="block text-[11px] font-medium text-slate-500 mb-1">Name</label><input className="input-field !text-xs !py-1.5 !w-44" placeholder="Participant / nominee" value={invite.name} onChange={(e) => setInvite((p) => ({ ...p, name: e.target.value }))} /></div>
            <div><label className="block text-[11px] font-medium text-slate-500 mb-1">Email</label><input className="input-field !text-xs !py-1.5 !w-60" placeholder="them@example.com" value={invite.email} onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))} /></div>
            <button onClick={sendInvite} disabled={portalBusy === 'invite'} className="btn-primary !text-xs !px-3 !py-2 flex items-center gap-1.5 disabled:opacity-50">{portalBusy === 'invite' ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Send invite</button>
          </div>
          {portalAccounts.length === 0 ? (
            <p className="text-xs text-slate-400">No portal access yet — invite the participant (or their nominee) above.</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {portalAccounts.map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-slate-700 font-medium">{a.name || a.email}</span>
                    <span className="text-[11px] text-slate-400 block">{a.email}{a.last_login ? ` · last sign-in ${fmtD(a.last_login)}` : ''}</span>
                  </div>
                  <span className={clsx('badge !text-[10px]', a.status === 'active' ? 'badge-green' : a.status === 'revoked' ? 'badge-red' : 'badge-yellow')}>{cap(a.status)}</span>
                  {a.status !== 'revoked' && <button onClick={() => resendInvite(a.id)} disabled={!!portalBusy} className="text-[11px] text-primary-600 hover:underline disabled:opacity-40">Resend invite</button>}
                  {a.status !== 'revoked' && <button onClick={() => revokeAccess(a.id)} disabled={!!portalBusy} title="Revoke access" className="text-slate-300 hover:text-rose-600 disabled:opacity-40"><X size={14} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
